'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { AnimatedSection } from '@/components/shared/animated-section';

export function FeaturesClient() {
  return (
    <>
      {/* Hero */}
      <section className="gradient-bg py-20 md:py-32 mt-16">
        <div className="container max-w-4xl text-center space-y-6">
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="gradient-text">AI visibility with financial intelligence</span>
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            AI Ops meets FinOps. Track usage, optimize &amp; allocate costs, and measure ROI across
            every AI provider — all in one place.
          </motion.p>
        </div>
      </section>

      {/* Feature 1: Unified Spend Tracking */}
      <AnimatedSection>
        <FeatureSection
          title="Unified AI Spend Tracking"
          description="Connect all your AI providers in one place. Get a complete view of spending across OpenAI, Anthropic, AWS Bedrock, Azure OpenAI, and 13+ more providers."
          features={[
            'One-click OAuth integration with major providers',
            'Automatic cost data sync every 15 minutes',
            'Unified dashboard across all providers',
            'Historical cost trends and forecasting',
          ]}
          imagePosition="right"
          mockup={<SpendTrackingMockup />}
        />
      </AnimatedSection>

      {/* Feature 2: Application-Level Insights */}
      <AnimatedSection>
        <FeatureSection
          title="Application-Level Insights with SDK"
          description="Go beyond provider-level tracking. Our lightweight SDK gives you application-level visibility into AI usage, costs, and performance."
          features={[
            'Add 5 lines of code to your app',
            'Track usage by project, team, or feature',
            'Correlate costs with business outcomes',
            'Debug expensive queries in production',
          ]}
          imagePosition="left"
          mockup={<SDKCodeMockup />}
        />
      </AnimatedSection>

      {/* Feature 3: ROI Measurement */}
      <AnimatedSection>
        <FeatureSection
          title="Built-In ROI Measurement & Analytics"
          description="Finally answer 'Is AI worth it?' Track revenue impact, cost savings, and efficiency gains alongside your AI spending."
          features={[
            'Define ROI metrics per project',
            'Automatic ROI calculation',
            'Business outcome correlation',
            'Executive-ready reports',
          ]}
          imagePosition="right"
          mockup={<ROIAnalyticsMockup />}
        />
      </AnimatedSection>

      {/* Feature 4: Cost Optimization */}
      <AnimatedSection>
        <FeatureSection
          title="Cost Optimization & Alerts"
          description="Set budgets, receive alerts, and get actionable recommendations to reduce your AI spending without sacrificing performance."
          features={[
            'Budget alerts before you overspend',
            'Anomaly detection for unusual patterns',
            'Model recommendation engine',
            'Identify cost-saving opportunities',
          ]}
          imagePosition="left"
          mockup={<AlertsDashboardMockup />}
        />
      </AnimatedSection>

      {/* Feature 5: Untracked Usage Detection */}
      <AnimatedSection>
        <FeatureSection
          title="Identify Untracked AI Usage"
          description="Our unique reconciliation engine matches provider data with SDK metrics to find shadow AI usage and untagged spending."
          features={[
            'Reconcile provider vs. SDK data',
            'Find rogue API keys',
            'Identify forgotten test projects',
            'Close the visibility gap',
          ]}
          imagePosition="right"
          mockup={<ReconciliationMockup />}
          badge="Unique"
        />
      </AnimatedSection>

      {/* Feature 6: Governance */}
      <AnimatedSection>
        <FeatureSection
          title="Security & Governance"
          description="Enforce policies, manage access, and maintain compliance across all your AI initiatives."
          features={[
            'Role-based access control',
            'Audit logs for all activities',
            'Policy enforcement',
            'Compliance reporting',
          ]}
          imagePosition="left"
          mockup={<GovernanceMockup />}
        />
      </AnimatedSection>

      {/* CTA */}
      <AnimatedSection>
        <section className="py-20 gradient-bg">
          <div className="container max-w-4xl text-center space-y-8">
            <h2 className="text-3xl sm:text-5xl font-bold">
              Ready to Get Complete <span className="gradient-text">AI Visibility?</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Start for free today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/handler/sign-up"
                className="gradient-primary text-white px-8 py-4 rounded-lg font-semibold text-lg hover:opacity-90 transition-opacity inline-block"
              >
                Start Free
              </Link>
              <Link
                href="/#pricing"
                className="bg-white border-2 border-gray-200 px-8 py-4 rounded-lg font-semibold text-lg hover:border-primary/30 transition-colors inline-block"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </section>
      </AnimatedSection>
    </>
  );
}

function FeatureSection({
  title,
  description,
  features,
  imagePosition,
  mockup,
  badge,
}: {
  title: string;
  description: string;
  features: string[];
  imagePosition: 'left' | 'right';
  mockup: React.ReactNode;
  badge?: string;
}) {
  return (
    <section className="py-20 bg-white odd:gradient-bg">
      <div className="container max-w-7xl">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
            imagePosition === 'left' ? 'lg:flex-row-reverse' : ''
          }`}
        >
          {/* Content */}
          <div className={`space-y-6 ${imagePosition === 'left' ? 'lg:order-2' : ''}`}>
            <div>
              {badge && (
                <span className="bg-linear-to-r from-orange-500 to-red-500 text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                  {badge}
                </span>
              )}
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">{title}</h2>
              <p className="text-lg text-muted-foreground">{description}</p>
            </div>

            <ul className="space-y-3">
              {features.map((feature) => (
                <li key={feature} className="flex items-start gap-3">
                  <svg
                    className="w-6 h-6 text-green-500 shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Mockup */}
          <div className={`${imagePosition === 'left' ? 'lg:order-1' : ''}`}>
            <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200">
              {mockup}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// SVG Mockups
function SpendTrackingMockup() {
  return (
    <svg viewBox="0 0 800 500" className="w-full h-auto">
      <rect width="800" height="500" fill="#f9fafb" />
      <text x="24" y="40" fontSize="20" fontWeight="600" fill="#1a1a1a">
        AI Spend Overview
      </text>

      {/* Provider Cards */}
      <rect x="24" y="70" width="240" height="100" rx="8" fill="#ffffff" />
      <text x="40" y="100" fontSize="14" fill="#6b7280">
        OpenAI
      </text>
      <text x="40" y="130" fontSize="24" fontWeight="700" fill="#6366f1">
        $8,450
      </text>
      <text x="40" y="150" fontSize="12" fill="#10b981">
        34% of total
      </text>

      <rect x="280" y="70" width="240" height="100" rx="8" fill="#ffffff" />
      <text x="296" y="100" fontSize="14" fill="#6b7280">
        Anthropic
      </text>
      <text x="296" y="130" fontSize="24" fontWeight="700" fill="#8b5cf6">
        $12,890
      </text>
      <text x="296" y="150" fontSize="12" fill="#10b981">
        52% of total
      </text>

      <rect x="536" y="70" width="240" height="100" rx="8" fill="#ffffff" />
      <text x="552" y="100" fontSize="14" fill="#6b7280">
        AWS Bedrock
      </text>
      <text x="552" y="130" fontSize="24" fontWeight="700" fill="#ec4899">
        $3,227
      </text>
      <text x="552" y="150" fontSize="12" fill="#6b7280">
        14% of total
      </text>

      {/* Chart */}
      <rect x="24" y="200" width="752" height="280" rx="8" fill="#ffffff" />
      <text x="40" y="230" fontSize="16" fontWeight="600" fill="#1a1a1a">
        Spend Trend (Last 30 Days)
      </text>

      {/* Line Chart Visualization */}
      <polyline
        points="60,400 150,350 240,380 330,320 420,340 510,280 600,300 690,250"
        fill="none"
        stroke="#6366f1"
        strokeWidth="3"
      />
      <polyline
        points="60,420 150,380 240,400 330,360 420,380 510,340 600,360 690,320"
        fill="none"
        stroke="#8b5cf6"
        strokeWidth="3"
      />
    </svg>
  );
}

function SDKCodeMockup() {
  return (
    <svg viewBox="0 0 800 500" className="w-full h-auto">
      <rect width="800" height="500" fill="#1e1e1e" />

      {/* Code Editor Header */}
      <rect width="800" height="40" fill="#2d2d2d" />
      <text x="20" y="25" fontSize="14" fill="#ffffff">
        app.py
      </text>

      {/* Code */}
      <text x="20" y="70" fontSize="14" fontFamily="monospace" fill="#4ec9b0">
        from
      </text>
      <text x="70" y="70" fontSize="14" fontFamily="monospace" fill="#dcdcdc">
        opsvis
      </text>
      <text x="140" y="70" fontSize="14" fontFamily="monospace" fill="#4ec9b0">
        import
      </text>
      <text x="200" y="70" fontSize="14" fontFamily="monospace" fill="#dcdcdc">
        track_ai_usage
      </text>

      <text x="20" y="110" fontSize="14" fontFamily="monospace" fill="#6a9955">
        # Track OpenAI call
      </text>

      <text x="20" y="140" fontSize="14" fontFamily="monospace" fill="#c586c0">
        @track_ai_usage
      </text>
      <text x="150" y="140" fontSize="14" fontFamily="monospace" fill="#9cdcfe">
        (
      </text>

      <text x="20" y="170" fontSize="14" fontFamily="monospace" fill="#dcdcdc">
        {'    '}project
      </text>
      <text x="120" y="170" fontSize="14" fontFamily="monospace" fill="#ce9178">
        {`="customer-support"`}
      </text>
      <text x="320" y="170" fontSize="14" fontFamily="monospace" fill="#9cdcfe">
        ,
      </text>

      <text x="20" y="200" fontSize="14" fontFamily="monospace" fill="#dcdcdc">
        {'    '}team
      </text>
      <text x="120" y="200" fontSize="14" fontFamily="monospace" fill="#ce9178">
        {`="product"`}
      </text>

      <text x="20" y="230" fontSize="14" fontFamily="monospace" fill="#9cdcfe">
        )
      </text>

      <text x="20" y="260" fontSize="14" fontFamily="monospace" fill="#569cd6">
        def
      </text>
      <text x="70" y="260" fontSize="14" fontFamily="monospace" fill="#dcdcaa">
        {' '}
        generate_response
      </text>
      <text x="250" y="260" fontSize="14" fontFamily="monospace" fill="#9cdcfe">
        (prompt):
      </text>

      <text x="40" y="290" fontSize="14" fontFamily="monospace" fill="#dcdcdc">
        response = client.chat.completions.create(...)
      </text>

      <text x="40" y="320" fontSize="14" fontFamily="monospace" fill="#c586c0">
        return
      </text>
      <text x="110" y="320" fontSize="14" fontFamily="monospace" fill="#dcdcdc">
        {' '}
        response
      </text>

      {/* Tracking Info */}
      <rect x="20" y="370" width="760" height="100" rx="8" fill="#2d2d2d" />
      <text x="40" y="400" fontSize="13" fill="#4ec9b0">
        ✓ Tracked: $0.0042 • 1,250 tokens • 340ms latency
      </text>
      <text x="40" y="430" fontSize="12" fill="#6b7280">
        Project: customer-support | Team: product | Model: gpt-4
      </text>
    </svg>
  );
}

function ROIAnalyticsMockup() {
  return (
    <svg viewBox="0 0 800 500" className="w-full h-auto">
      <rect width="800" height="500" fill="#f9fafb" />
      <text x="24" y="40" fontSize="20" fontWeight="600" fill="#1a1a1a">
        ROI Analytics Dashboard
      </text>

      {/* ROI Cards */}
      <rect x="24" y="70" width="370" height="120" rx="8" fill="#ffffff" />
      <text x="40" y="100" fontSize="14" fill="#6b7280">
        Customer Support Bot ROI
      </text>
      <text x="40" y="140" fontSize="32" fontWeight="700" fill="#10b981">
        4.2x
      </text>
      <text x="40" y="168" fontSize="12" fill="#6b7280">
        $12,400 cost • $52,000 value generated
      </text>

      <rect x="410" y="70" width="366" height="120" rx="8" fill="#ffffff" />
      <text x="426" y="100" fontSize="14" fill="#6b7280">
        Code Assistant ROI
      </text>
      <text x="426" y="140" fontSize="32" fontWeight="700" fill="#10b981">
        2.8x
      </text>
      <text x="426" y="168" fontSize="12" fill="#6b7280">
        $8,200 cost • $23,000 time saved
      </text>

      {/* Chart */}
      <rect x="24" y="220" width="752" height="260" rx="8" fill="#ffffff" />
      <text x="40" y="250" fontSize="16" fontWeight="600" fill="#1a1a1a">
        ROI Trend by Project
      </text>

      {/* Bar Groups */}
      <rect x="80" y="350" width="50" height="80" rx="4" fill="#10b981" />
      <rect x="140" y="320" width="50" height="110" rx="4" fill="#10b981" />

      <rect x="250" y="360" width="50" height="70" rx="4" fill="#6366f1" />
      <rect x="310" y="340" width="50" height="90" rx="4" fill="#6366f1" />

      <rect x="420" y="370" width="50" height="60" rx="4" fill="#8b5cf6" />
      <rect x="480" y="350" width="50" height="80" rx="4" fill="#8b5cf6" />

      <text x="90" y="455" fontSize="11" fill="#6b7280">
        Support
      </text>
      <text x="260" y="455" fontSize="11" fill="#6b7280">
        Code Gen
      </text>
      <text x="430" y="455" fontSize="11" fill="#6b7280">
        Content
      </text>
    </svg>
  );
}

function AlertsDashboardMockup() {
  return (
    <svg viewBox="0 0 800 500" className="w-full h-auto">
      <rect width="800" height="500" fill="#f9fafb" />
      <text x="24" y="40" fontSize="20" fontWeight="600" fill="#1a1a1a">
        Alerts &amp; Monitoring
      </text>

      {/* Alert Cards */}
      <rect x="24" y="70" width="752" height="80" rx="8" fill="#fef3c7" />
      <text x="40" y="105" fontSize="16" fontWeight="600" fill="#92400e">
        ⚠ Budget Alert: OpenAI
      </text>
      <text x="40" y="130" fontSize="14" fill="#92400e">
        {`You've used 87% of your $10,000 monthly budget ($8,700 spent)`}
      </text>

      <rect x="24" y="170" width="752" height="80" rx="8" fill="#fee2e2" />
      <text x="40" y="205" fontSize="16" fontWeight="600" fill="#7f1d1d">
        🚨 Anomaly Detected: Unusual Spike
      </text>
      <text x="40" y="230" fontSize="14" fill="#7f1d1d">
        AWS Bedrock usage increased 340% in the last hour
      </text>

      <rect x="24" y="270" width="752" height="80" rx="8" fill="#d1fae5" />
      <text x="40" y="305" fontSize="16" fontWeight="600" fill="#065f46">
        💡 Optimization Opportunity
      </text>
      <text x="40" y="330" fontSize="14" fill="#065f46">
        Switch to GPT-3.5-turbo for 40% of queries to save $1,200/month
      </text>

      {/* Real-time Monitor */}
      <rect x="24" y="370" width="752" height="110" rx="8" fill="#ffffff" />
      <text x="40" y="400" fontSize="16" fontWeight="600" fill="#1a1a1a">
        Real-Time Usage
      </text>
      <text x="40" y="430" fontSize="14" fill="#6b7280">
        Last 5 minutes: 1,247 requests • $24.50 spent
      </text>
      <rect x="40" y="445" width="400" height="20" rx="10" fill="#e5e7eb" />
      <rect x="40" y="445" width="280" height="20" rx="10" fill="#6366f1" />
      <text x="460" y="460" fontSize="12" fill="#6b7280">
        70% of daily budget used
      </text>
    </svg>
  );
}

function ReconciliationMockup() {
  return (
    <svg viewBox="0 0 800 500" className="w-full h-auto">
      <rect width="800" height="500" fill="#f9fafb" />
      <text x="24" y="40" fontSize="20" fontWeight="600" fill="#1a1a1a">
        Usage Reconciliation
      </text>

      {/* Comparison */}
      <rect x="24" y="70" width="370" height="180" rx="8" fill="#ffffff" />
      <text x="40" y="100" fontSize="16" fontWeight="600" fill="#1a1a1a">
        Provider Data (OpenAI)
      </text>
      <text x="40" y="135" fontSize="24" fontWeight="700" fill="#6366f1">
        $24,567
      </text>
      <text x="40" y="160" fontSize="14" fill="#6b7280">
        March 2024 total spend
      </text>
      <text x="40" y="185" fontSize="14" fill="#6b7280">
        12.4M tokens processed
      </text>
      <text x="40" y="210" fontSize="14" fill="#6b7280">
        47,890 API calls
      </text>

      <rect x="410" y="70" width="366" height="180" rx="8" fill="#ffffff" />
      <text x="426" y="100" fontSize="16" fontWeight="600" fill="#1a1a1a">
        SDK Tracked Usage
      </text>
      <text x="426" y="135" fontSize="24" fontWeight="700" fill="#8b5cf6">
        $22,140
      </text>
      <text x="426" y="160" fontSize="14" fill="#6b7280">
        Tracked via OpsVis SDK
      </text>
      <text x="426" y="185" fontSize="14" fill="#6b7280">
        11.2M tokens tracked
      </text>
      <text x="426" y="210" fontSize="14" fill="#6b7280">
        43,220 tracked calls
      </text>

      {/* Gap Alert */}
      <rect x="24" y="270" width="752" height="200" rx="8" fill="#fef3c7" />
      <text x="40" y="305" fontSize="18" fontWeight="600" fill="#92400e">
        ⚠ $2,427 Untracked Usage Detected
      </text>
      <text x="40" y="340" fontSize="14" fill="#92400e">
        Provider reports more spending than SDK tracked. Possible causes:
      </text>

      <text x="60" y="375" fontSize="14" fill="#92400e">
        • 3 untagged API keys found (likely from test environments)
      </text>
      <text x="60" y="400" fontSize="14" fill="#92400e">
        • 1 legacy project not using SDK
      </text>
      <text x="60" y="425" fontSize="14" fill="#92400e">
        • 1 rogue developer API key
      </text>

      <rect x="40" y="440" width="180" height="36" rx="6" fill="#f59e0b" />
      <text x="130" y="462" fontSize="14" fontWeight="600" fill="#ffffff" textAnchor="middle">
        Investigate Now
      </text>
    </svg>
  );
}

function GovernanceMockup() {
  return (
    <svg viewBox="0 0 800 500" className="w-full h-auto">
      <rect width="800" height="500" fill="#f9fafb" />
      <text x="24" y="40" fontSize="20" fontWeight="600" fill="#1a1a1a">
        Security &amp; Governance
      </text>

      {/* Access Control */}
      <rect x="24" y="70" width="370" height="200" rx="8" fill="#ffffff" />
      <text x="40" y="100" fontSize="16" fontWeight="600" fill="#1a1a1a">
        Team Access Control
      </text>

      <rect x="40" y="120" width="330" height="40" rx="6" fill="#f9fafb" />
      <text x="56" y="145" fontSize="13" fill="#1a1a1a">
        Engineering Team (12 members)
      </text>
      <text x="300" y="145" fontSize="12" fill="#10b981">
        Admin
      </text>

      <rect x="40" y="170" width="330" height="40" rx="6" fill="#f9fafb" />
      <text x="56" y="195" fontSize="13" fill="#1a1a1a">
        Finance Team (4 members)
      </text>
      <text x="300" y="195" fontSize="12" fill="#6366f1">
        Viewer
      </text>

      <rect x="40" y="220" width="330" height="40" rx="6" fill="#f9fafb" />
      <text x="56" y="245" fontSize="13" fill="#1a1a1a">
        Product Team (8 members)
      </text>
      <text x="300" y="245" fontSize="12" fill="#8b5cf6">
        Editor
      </text>

      {/* Audit Log */}
      <rect x="410" y="70" width="366" height="200" rx="8" fill="#ffffff" />
      <text x="426" y="100" fontSize="16" fontWeight="600" fill="#1a1a1a">
        Recent Activity
      </text>

      <text x="426" y="130" fontSize="12" fill="#6b7280">
        2 min ago • Sarah Chen added API key
      </text>
      <text x="426" y="155" fontSize="12" fill="#6b7280">
        15 min ago • Budget alert sent
      </text>
      <text x="426" y="180" fontSize="12" fill="#6b7280">
        1 hour ago • New project created
      </text>
      <text x="426" y="205" fontSize="12" fill="#6b7280">
        3 hours ago • Access granted to Finance
      </text>
      <text x="426" y="230" fontSize="12" fill="#6b7280">
        5 hours ago • Policy updated
      </text>

      {/* Compliance */}
      <rect x="24" y="290" width="752" height="190" rx="8" fill="#ffffff" />
      <text x="40" y="320" fontSize="16" fontWeight="600" fill="#1a1a1a">
        Compliance Status
      </text>

      <rect x="40" y="340" width="240" height="120" rx="8" fill="#d1fae5" />
      <text x="56" y="370" fontSize="14" fontWeight="600" fill="#065f46">
        ✓ SOC 2 Compliant
      </text>
      <text x="56" y="395" fontSize="12" fill="#065f46">
        All requirements met
      </text>
      <text x="56" y="415" fontSize="12" fill="#065f46">
        Last audit: Jan 2024
      </text>

      <rect x="300" y="340" width="240" height="120" rx="8" fill="#d1fae5" />
      <text x="316" y="370" fontSize="14" fontWeight="600" fill="#065f46">
        ✓ GDPR Compliant
      </text>
      <text x="316" y="395" fontSize="12" fill="#065f46">
        Data retention policies active
      </text>
      <text x="316" y="415" fontSize="12" fill="#065f46">
        EU data in Frankfurt region
      </text>
    </svg>
  );
}
